import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { t } from '@ping/helpers';
import { ButtonA11Y, Modal, Text } from '@ping/uikit';

import { REVIEW_DOCUMENT_ACCEPT, useReviewDocuments } from '../use-review-documents';

import type { ActiveBusinessReviewNotice } from '../active-business-review';
import type { IModalContext } from '@ping/uikit';

import style from './style.module.scss';

interface IActiveBusinessReviewModalProps {
  ctx: IModalContext;
  notice: ActiveBusinessReviewNotice;
  comment?: string | null;
  onSubmitted: () => void;
  uploadDocument?: (file: File) => Promise<unknown>;
  submitReview?: () => Promise<unknown>;
}

export const ActiveBusinessReviewModal = ({
  ctx,
  notice,
  comment,
  onSubmitted,
  uploadDocument,
  submitReview,
}: IActiveBusinessReviewModalProps) => {
  const [submittedNames, setSubmittedNames] = useState<string[] | null>(null);
  const documents = useReviewDocuments({
    onSubmitted: setSubmittedNames,
    uploadDocument,
    submitReview,
  });
  const note = comment?.trim();
  const isOpen = ctx.state.isOpen;

  // Refreshing the status earlier would swap the confirmation for the pending notice while it is open.
  useEffect(() => {
    if (!isOpen && submittedNames) {
      setSubmittedNames(null);
      onSubmitted();
    }
  }, [isOpen, submittedNames, onSubmitted]);

  if (submittedNames) {
    return (
      <Modal.Portal className={style['modal']} divide>
        <Modal.Header className={style['modal__header']}>
          <Modal.Title>{t('Documents submitted')}</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>

        <Modal.Content className={style['modal__content']}>
          <div className={style['modal__confirmation']} role='status'>
            <CheckCircle2 aria-hidden className={style['modal__confirmation-icon']} />
            <Text heading='4'>{t('Your documents were sent for review')}</Text>
            <Text className={style['modal__description']}>
              {t(
                'Compliance will review them and update the status of this business. You can follow the review from the header.'
              )}
            </Text>
          </div>
          <div className={style['modal__uploads']}>
            {submittedNames.map((name, index) => (
              <div className={style['modal__upload']} key={`${name}-${index}`}>
                <Text body='regular'>{name}</Text>
                <Text caption='regular' className={style['modal__upload-status']}>
                  {t('Submitted')}
                </Text>
              </div>
            ))}
          </div>
        </Modal.Content>

        <Modal.Actions className={style['modal__actions']}>
          <ButtonA11Y primary onPress={() => ctx.state.close()}>
            {t('Done')}
          </ButtonA11Y>
        </Modal.Actions>
      </Modal.Portal>
    );
  }

  return (
    <Modal.Portal className={style['modal']} divide>
      <Modal.Header className={style['modal__header']}>
        <Modal.Title>
          {notice === 'more-data' ? t('Provide requested documents') : t('Business compliance review')}
        </Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>

      <Modal.Content className={style['modal__content']}>
        {notice === 'more-data' ? (
          <>
            {note ? (
              <div className={style['modal__note']}>
                <Text caption='regular' className={style['modal__label']}>
                  {t('Compliance comment')}
                </Text>
                <Text body='regular'>{note}</Text>
              </div>
            ) : (
              <Text className={style['modal__description']}>
                {t('Compliance asked for additional documents for this business.')}
              </Text>
            )}
            <input
              ref={documents.inputRef}
              accept={REVIEW_DOCUMENT_ACCEPT}
              hidden
              type='file'
              onChange={event => {
                void documents.handleFiles(event.target.files);
              }}
            />
            {documents.uploadedNames.length > 0 ? (
              <div className={style['modal__uploads']}>
                {documents.uploadedNames.map((name, index) => (
                  <div className={style['modal__upload']} key={`${name}-${index}`}>
                    <Text body='regular'>{name}</Text>
                    <Text caption='regular'>{t('Uploaded')}</Text>
                  </div>
                ))}
              </div>
            ) : null}
            {documents.error ? <Text className={style['modal__error']}>{documents.error}</Text> : null}
          </>
        ) : (
          <Text className={style['modal__description']}>
            {notice === 'open'
              ? t('Compliance is reviewing this business.')
              : t('The documents were sent. Compliance will review them shortly.')}
          </Text>
        )}
      </Modal.Content>

      {notice === 'more-data' ? (
        <Modal.Actions className={style['modal__actions']}>
          <ButtonA11Y
            isDisabled={documents.busy}
            isLoading={documents.busy && !documents.submitting}
            secondary
            onPress={documents.openPicker}
          >
            {t('Upload a document')}
          </ButtonA11Y>
          <ButtonA11Y
            isDisabled={!documents.canSubmit}
            isLoading={documents.submitting}
            primary
            onPress={() => void documents.handleSubmit()}
          >
            {t('Submit documents')}
          </ButtonA11Y>
        </Modal.Actions>
      ) : null}
    </Modal.Portal>
  );
};
